import * as React from 'react';
import { useSelector } from 'react-redux';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import { MoonOutlined, PlusOutlined, SunOutlined } from '@ant-design/icons';
import { RootState } from '../../../reducers/reducers';
import { selectOrg, selectUserIsRunwayAdmin } from '../../../selectors/selectors';
import { AppGroup } from '../../../types';
import { getAppGroupsForOrgId } from '../../../helpers';
import { displayUserNameLong } from '../../../helpers/displayHelpers';
import { antGrey, runwayColors, navSectionTitleStyle } from '../../styles/globalStyles';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import LogoWordmark from '../../../../assets/runway-logo-horizontal.svg';
import { useThemeMode } from '../../styles/themeMode';
import Button from 'antd/lib/button';
import { AppGroupRow } from './AppGroupRow';
import { OrgMetricsSection, UserSection, InviteSection } from './ProductDropdownSections';

interface ReleaseManagementSectionProps {
    orgId: string;
    appGroups: AppGroup[] | null | undefined;
    currentAppGroupId: string | null | undefined;
    currentAppId: string | undefined;
    currentAppGroupAllAppNamesAreSame?: boolean;
    onAppGroupSelect: (appGroupId: string) => void;
    onPlatformSelect: (appEntityId: string, appGroupId: string) => void;
    onSettingsClick: (appIdParam: string) => void;
    onAddProduct: () => void;
}

const ReleaseManagementSection: React.FC<ReleaseManagementSectionProps> = ({
    orgId,
    appGroups,
    currentAppGroupId,
    currentAppId,
    currentAppGroupAllAppNamesAreSame,
    onAppGroupSelect,
    onPlatformSelect,
    onSettingsClick,
    onAddProduct,
}) => {
    if (!appGroups) return null;

    const filteredAppGroups = appGroups.filter((appGroup) => appGroup.appIds?.length > 0);

    return (
        <Flex vertical>
            <Text style={navSectionTitleStyle}>Release Management</Text>

            <div
                style={{
                    maxHeight: '30vh',
                    overflowY: 'auto',
                }}
            >
                {filteredAppGroups.map((appGroup) => (
                    <AppGroupRow
                        key={appGroup.id}
                        orgId={orgId}
                        appGroupId={appGroup.id}
                        isSelected={appGroup.id === currentAppGroupId}
                        currentAppId={currentAppId}
                        allAppNamesAreSame={
                            appGroup.id === currentAppGroupId ? currentAppGroupAllAppNamesAreSame : undefined
                        }
                        onSelect={() => onAppGroupSelect(appGroup.id)}
                        onPlatformSelect={(appEntityId) => onPlatformSelect(appEntityId, appGroup.id)}
                        onSettingsClick={onSettingsClick}
                    />
                ))}
            </div>

            <AddProductButton onClick={onAddProduct} />
        </Flex>
    );
};

const AddProductButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
        <HoverableNavItem onClick={onClick}>
            <Flex align="center" gap={12}>
                <Flex
                    align="center"
                    justify="center"
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: `1px solid ${antGrey.grey7}`,
                    }}
                >
                    <PlusOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />
                </Flex>
                <Text style={{ color: antGrey.grey6 }}>Add a product...</Text>
            </Flex>
        </HoverableNavItem>
    );
};

export interface ProductDropdownProps {
    orgId: string;
    currentAppGroupId: string | null | undefined;
    currentAppId: string | undefined;
    currentAppGroupAllAppNamesAreSame?: boolean;
    onAppGroupSelect: (appGroupId: string) => void;
    onPlatformSelect: (appEntityId: string, appGroupId: string) => void;
    onSettingsClick: (appIdParam: string) => void;
    onAddProduct: () => void;
    onOrgClick: () => void;
    onUserClick: () => void;
    onInviteClick: () => void;
}

export const ProductDropdown: React.FC<ProductDropdownProps> = ({
    orgId,
    currentAppGroupId,
    currentAppId,
    currentAppGroupAllAppNamesAreSame,
    onAppGroupSelect,
    onPlatformSelect,
    onSettingsClick,
    onAddProduct,
    onOrgClick,
    onUserClick,
    onInviteClick,
}) => {
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const user = useSelector((state: RootState) => state.user);
    const appGroups = useSelector((state: RootState) => getAppGroupsForOrgId(orgId, state));
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const { mode, toggleMode } = useThemeMode();

    if (!org) return null;

    const showInvite = !org.ssoSamlConfig?.ssoSamlEnforced;
    const orgLetter = org.name?.charAt(0).toUpperCase() || 'O';
    const userName = displayUserNameLong(user);
    const userLetter = userName.charAt(0).toUpperCase();

    return (
        <div
            style={{
                backgroundColor: 'black',
                borderRadius: 8,
                border: `1px solid ${runwayColors.navGrey}`,
                minWidth: '25rem',
            }}
        >
            <ReleaseManagementSection
                orgId={orgId}
                appGroups={appGroups}
                currentAppGroupId={currentAppGroupId}
                currentAppId={currentAppId}
                currentAppGroupAllAppNamesAreSame={currentAppGroupAllAppNamesAreSame}
                onAppGroupSelect={onAppGroupSelect}
                onPlatformSelect={onPlatformSelect}
                onSettingsClick={onSettingsClick}
                onAddProduct={onAddProduct}
            />

            <Divider style={{ borderColor: runwayColors.navGrey, margin: 0 }} />
            <OrgMetricsSection
                orgLetter={orgLetter}
                orgName={org.name}
                orgImageUrl={org.orgImageUrl}
                onOrgClick={onOrgClick}
            />

            <Divider style={{ borderColor: runwayColors.navGrey, margin: 0 }} />
            <UserSection
                userLetter={userLetter}
                userName={userName}
                userAvatarUrl={user.commsAvatars?.image48}
                onUserClick={onUserClick}
            />

            {showInvite && (
                <>
                    <Divider style={{ borderColor: runwayColors.navGrey, margin: 0 }} />
                    <InviteSection onInviteClick={onInviteClick} />
                </>
            )}

            {isRunwayAdmin && (
                <Flex align="center" justify="flex-end" gap={10} style={{ padding: '8px 16px' }}>
                    <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                        <Button
                            type="text"
                            className="product-navigator-icon-btn"
                            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            onClick={toggleMode}
                            style={{ padding: 0, height: 'auto', color: antGrey.grey6, justifyContent: 'end' }}
                            icon={
                                mode === 'dark' ?
                                    <SunOutlined style={{ fontSize: 16 }} />
                                :   <MoonOutlined style={{ fontSize: 16 }} />
                            }
                        />
                    </Tooltip>
                </Flex>
            )}

            <Flex justify="flex-end" style={{ padding: 16 }}>
                <img src={LogoWordmark} alt="Runway" height={16} />
            </Flex>
        </div>
    );
};
